import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import api from '../../utils/api';

const PAGE_LIMIT = 20;
const EMPTY_FILTERS = { flagId: '', status: '', source: '', flagType: '' };

// KPI drilldown views opened from the Deposit Flag Review cards. Each shows a
// fixed, date-scoped list (no manual filter bar) via /flags/kpi?type=...
const KPI_VIEWS = {
  'shared-card': {
    title: 'Shared Card Flags',
    desc: 'Flags where the same card was used across multiple deposits',
  },
  overlap: {
    title: 'Overlap Cases',
    desc: 'Flags where one customer is linked to many inmates',
  },
  open: {
    title: 'Open Flags',
    desc: 'Flags currently in Open status',
  },
  critical: {
    title: 'Critical Flags',
    desc: 'Non-closed flags at critical risk',
  },
};

// "shared-card" -> "Shared card"
const humanizeFlagType = (ft) =>
  String(ft)
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());

const Flags = () => {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_LIMIT, total: 0, pages: 0 });

  // Committed filters used for fetching, and the live flag-id text box.
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [flagIdText, setFlagIdText] = useState('');
  const [options, setOptions] = useState({ statuses: [], sources: [], flagTypes: [] });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const typeFilter = searchParams.get('type'); // KPI view key, e.g. 'shared-card'
  const kpiView = KPI_VIEWS[typeFilter] || null;
  const isKpiView = Boolean(kpiView);
  // Date range carried over from the Deposit Flag Review KPI click, so the
  // list matches the same count the KPI showed.
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');

  // Load dropdown options once (only relevant for the full list view).
  useEffect(() => {
    if (isKpiView) return;
    api
      .get('/flags/filter-options')
      .then((res) => {
        if (res.data && res.data.success) setOptions(res.data.data || { statuses: [], sources: [], flagTypes: [] });
      })
      .catch((err) => console.error('Error fetching filter options:', err));
  }, [isKpiView]);

  // Reset paging/filters when switching views or when the date range changes.
  useEffect(() => {
    setPage(1);
    setFilters(EMPTY_FILTERS);
    setFlagIdText('');
  }, [typeFilter, startDate, endDate]);

  useEffect(() => {
    fetchFlags(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, page, filters, startDate, endDate]);

  const fetchFlags = async (targetPage = 1) => {
    try {
      setLoading(true);
      const endpoint = isKpiView ? '/flags/kpi' : '/flags';
      const params = { page: targetPage, limit: PAGE_LIMIT };
      if (isKpiView) {
        params.type = typeFilter;
        // Carry the KPI's date filter so the list count matches the KPI.
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
      } else {
        if (filters.flagId) params.flagId = filters.flagId;
        if (filters.status) params.status = filters.status;
        if (filters.source) params.source = filters.source;
        if (filters.flagType) params.flagType = filters.flagType;
      }
      const response = await api.get(endpoint, { params });
      if (response.data && response.data.success) {
        setFlags(response.data.data || []);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        } else {
          const data = response.data.data || [];
          setPagination({ page: 1, limit: PAGE_LIMIT, total: data.length, pages: 1 });
        }
      } else {
        setFlags(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching flags:', error);
      setFlags([]);
      setPagination({ page: 1, limit: PAGE_LIMIT, total: 0, pages: 0 });
    } finally {
      setLoading(false);
    }
  };

  const applyFlagId = (e) => {
    if (e) e.preventDefault();
    setPage(1);
    setFilters((f) => ({ ...f, flagId: flagIdText.trim() }));
  };

  const changeStatus = (value) => {
    setPage(1);
    setFilters((f) => ({ ...f, status: value }));
  };

  const changeSource = (value) => {
    setPage(1);
    setFilters((f) => ({ ...f, source: value }));
  };

  const changeFlagType = (value) => {
    setPage(1);
    setFilters((f) => ({ ...f, flagType: value }));
  };

  const clearFilters = () => {
    setPage(1);
    setFlagIdText('');
    setFilters(EMPTY_FILTERS);
  };

  const hasActiveFilters = Boolean(filters.flagId || filters.status || filters.source || filters.flagType);

  const totalPages = pagination.pages || 0;
  const total = pagination.total || 0;
  const startIdx = total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const endIdx = Math.min(pagination.page * pagination.limit, total);

  const pageWindow = () => {
    if (totalPages <= 1) return [];
    const windowSize = 5;
    let start = Math.max(1, page - Math.floor(windowSize / 2));
    let end = Math.min(totalPages, start + windowSize - 1);
    start = Math.max(1, Math.min(start, end - windowSize + 1));
    const pages = [];
    for (let p = start; p <= end; p++) pages.push(p);
    return pages;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isKpiView ? kpiView.title : 'Flagged Incidents'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isKpiView ? kpiView.desc : 'Review and manage flagged incidents'}
            {isKpiView && (startDate || endDate) && (
              <span className="ml-1 text-gray-500">
                · {startDate || '…'} to {endDate || '…'}
              </span>
            )}
          </p>
        </div>
        {isKpiView && (
          <Link
            to="/flags"
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 whitespace-nowrap"
          >
            Show all flags
          </Link>
        )}
      </div>

      {/* Filters (full-list view only) */}
      {!isKpiView && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex flex-col md:flex-row md:items-end gap-3">
            <form onSubmit={applyFlagId} className="flex-1 min-w-[180px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">Flag ID</label>
              <div className="relative">
                <input
                  type="text"
                  value={flagIdText}
                  onChange={(e) => setFlagIdText(e.target.value)}
                  placeholder="e.g. FLAG-017263 or 17263"
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
            </form>

            <div className="flex-1 min-w-[160px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => changeStatus(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">All statuses</option>
                {options.statuses.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div className="flex-1 min-w-[180px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">Source</label>
              <select
                value={filters.source}
                onChange={(e) => changeSource(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">All sources</option>
                {options.sources.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={applyFlagId}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 whitespace-nowrap"
              >
                Apply
              </button>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 whitespace-nowrap"
                >
                  <X className="w-4 h-4" /> Clear
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : flags.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No flags found</p>
          </div>
        ) : (
          <>
            <div className="table-scroll">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Flag #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {flags.map((flag) => (
                    <tr
                      key={flag._id}
                      onClick={() => navigate(`/flags/${flag._id}`)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{flag.flagNumber}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{flag.title}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{flag.source}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{flag.severity}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{flag.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Showing <span className="font-medium">{startIdx}</span>–<span className="font-medium">{endIdx}</span>{' '}
                of <span className="font-medium">{total.toLocaleString()}</span> flags
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="inline-flex items-center px-2 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {pageWindow()[0] > 1 && (
                    <>
                      <button
                        onClick={() => setPage(1)}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                      >
                        1
                      </button>
                      {pageWindow()[0] > 2 && <span className="px-1 text-gray-400">…</span>}
                    </>
                  )}
                  {pageWindow().map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`px-3 py-1.5 text-sm border rounded-lg ${
                        p === page
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  {pageWindow()[pageWindow().length - 1] < totalPages && (
                    <>
                      {pageWindow()[pageWindow().length - 1] < totalPages - 1 && (
                        <span className="px-1 text-gray-400">…</span>
                      )}
                      <button
                        onClick={() => setPage(totalPages)}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="inline-flex items-center px-2 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Flags;
